'use client';
import { contextMeter, formatCost, formatSpeed } from '../../lib/displayFormat';
import { memo, useMemo, useRef, useState, type ReactNode, type RefObject } from 'react';
import { X, ArrowUp, FileText, Upload, ChevronRight, Clock, Pause } from 'lucide-react';
import { interpolate, useTranslation } from '../../lib/i18n';
import type { BrainCard, BrainQueueMemberItem, BrainRecalledInput, Profile } from '../../lib/types';
import { useInvitable } from '../../lib/queries';
import { useInviteParticipant } from '../../lib/mutations';
import { Progress } from '../../components/ui/shadcn/progress';
import { ConnectionStatus } from '../../components/ui/ConnectionStatus';
import { modelRetryShown } from 'elowen-plugin-shared/retryPresentation';
import { GoalStatusInline } from './GoalStatus';
import { ModelPicker } from './ModelPicker';
import { useBrainChat, useBrainChatStatus, useBrainChatTranscript } from './BrainChatProvider';
import { ReplyQuote, quoteSnippet } from './ReplyQuote';
import { useReplyQuotes } from './replyQuotes';
import type { PendingUpload } from './brainChatAttachments';
import { formatBytes, formatTokens } from '../../lib/format';
import { brainModelLabel, brainModelQualifiedLabel } from '../../lib/modelProvider';
import { PluginChatPickerNotice } from '../../lib/pluginChatUi';
import { statuslineHasStats, useModelControlHost } from './ChatStatusline';
import { PluginCardBlock } from './ChatCards';
import { ChatComposer } from './ChatComposer';
import type { ComposerMentions } from './ChatComposer';
import { ConversationMembersPopover, inviteRefusal } from './ConversationMembersPopover';
import { useConversationListController } from './useConversationListController';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/shadcn/popover';
import { cacheHitPct } from '../../lib/displayFormat';
import { Avatar } from '../../components/ui/Avatar';
import { AvatarStack } from '../../components/ui/AvatarStack';
import { profileAvatarUser } from './conversationListModel';
import { Button } from '../../components/ui/Button';
import { useToast } from '../../components/ui/Toast';
import { Item, ItemActions, ItemContent, ItemGroup, ItemMedia } from '../../components/ui/shadcn/item';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/shadcn/dropdown-menu';

const NO_GONE_QUOTES: ReadonlySet<string> = new Set();

function PendingReply({ session, messageId, owner, onCancel, onJump }: {
  session: string; messageId: string; owner: Profile | null; onCancel: () => void; onJump: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { turns } = useBrainChatTranscript();
  const quotes = useReplyQuotes(turns, session, owner, NO_GONE_QUOTES, messageId);
  return <div className="flex min-w-0 items-center gap-1" data-testid="chat-pending-reply">
    <div className="min-w-0 flex-1"><ReplyQuote quote={quotes.get(messageId) ?? { status: 'loading' }} onJump={() => { void onJump().catch(() => toast(t.brainChat.historyLoadError, 'error')); }} /></div>
    <Button type="button" variant="ghost" size="icon-sm" icon={X} onClick={onCancel} aria-label={t.common.cancel} title={t.common.cancel} />
  </div>;
}

function RecalledChoice({ row, session, owner, onRestore }: { row: BrainRecalledInput; session: string; owner: Profile | null; onRestore: (id: string) => void }) {
  const { t } = useTranslation();
  const { turns } = useBrainChatTranscript();
  const target = row.replyTo;
  const targetId = target?.id;
  const targetTurns = useMemo(() => turns.filter(turn => targetId !== undefined && turn.id === targetId), [turns, targetId]);
  const quote = useReplyQuotes(targetTurns, session, owner, NO_GONE_QUOTES, targetId).get(targetId ?? '');
  const text = row.composer?.text || row.text || row.composer?.attachments[0]?.name || t.brainChat.ux.restoreRecalledInput;
  const author = quote?.status === 'ready' ? quote.author.kind === 'agent' ? t.chat.roleElowen : quote.author.profile.name.trim() || quote.author.profile.username : null;
  return <DropdownMenuItem onSelect={() => onRestore(row.id)} className="min-w-0">
    <span className="flex min-w-0 flex-col gap-0.5">
      <span className="truncate">{quoteSnippet(text)}</span>
      {target ? <span className="truncate text-meta text-muted-foreground">{t.chat.replyTo}: {quote?.status === 'ready' ? `${author}: ${quoteSnippet(quote.text)}` : quote?.status === 'unavailable' ? t.chat.messageUnavailable : quote?.status === 'failed' ? t.brainChat.historyLoadError : t.common.loading}</span> : null}
    </span>
  </DropdownMenuItem>;
}

/** A file on its way into the project: the bytes the server has accepted, as a bar and a percentage, and a
 *  way to stop it. Progress moves once per part, so it is honest about what is stored rather than about
 *  what the browser has merely handed to the network. */
function UploadChip({ upload, onCancel }: { upload: PendingUpload; onCancel: () => void }) {
  const { t, locale } = useTranslation();
  const percent = upload.size > 0 ? Math.floor((upload.sent / upload.size) * 100) : 0;
  const label = interpolate(t.brainChat.uploadProgress, { name: upload.name });
  // Every byte is in: the commit is publishing the file, and a cancel could no longer take it back.
  const committing = upload.sent >= upload.size;
  return (
    <span
      data-upload-pending
      title={`${formatBytes(upload.sent, locale)} / ${formatBytes(upload.size, locale)}`}
      className="inline-flex w-50 max-w-full flex-col gap-1 rounded-md border border-border bg-muted py-1 pl-1.5 pr-1 text-small text-foreground"
    >
      <span className="flex items-center gap-1.5">
        <Upload size={13} className="shrink-0 text-muted-foreground" aria-hidden />
        <span className="min-w-0 flex-1 truncate">{upload.name}</span>
        <span className="shrink-0 tabular-nums text-muted-foreground">{interpolate(t.brainChat.uploadPercent, { percent })}</span>
        {committing ? null : (
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            onClick={onCancel}
            aria-label={interpolate(t.brainChat.uploadCancel, { name: upload.name })}
            className="-mx-2 -my-1.5 shrink-0 hover:bg-transparent hover:text-foreground"
          >
            <X size={11} aria-hidden />
          </Button>
        )}
      </span>
      <Progress value={percent} aria-label={label} className="h-1" />
    </span>
  );
}

/** The queued messages above the composer, one calm line each: a clock (or the writer's face when the
 *  conversation is shared), the writer's name when there is one, the text, and the remove control where THIS
 *  viewer may use it (their own entry, or any for the owner). The list says "queued" through its label and
 *  counts itself as a list; an entry waiting for the run to finish carries an hourglass naming that state. */
export function QueueChips({ entries, onRemove, onSteer, className = '' }: { entries: readonly BrainQueueMemberItem[]; onRemove: (id: string) => void; onSteer: (id: string) => Promise<void>; className?: string }) {
  const { t } = useTranslation();
  const pending = useRef(new Set<string>());
  const [pendingIds, setPendingIds] = useState<ReadonlySet<string>>(new Set());
  const steer = (id: string): void => {
    if (pending.current.has(id)) return;
    pending.current.add(id);
    setPendingIds(new Set(pending.current));
    void onSteer(id).finally(() => { pending.current.delete(id); setPendingIds(new Set(pending.current)); });
  };
  return (
    <div className={className}>
      <ItemGroup aria-label={t.brainChat.queued} className="gap-1">
        {entries.map((q) => (
          <Item key={q.id} variant="muted" size="xs" data-testid="chat-queued-chip" className="flex-nowrap text-small">
            <ItemMedia>
              {q.author
                ? <Avatar user={{ ...q.author, avatar: q.author.avatar ?? undefined }} size={16} />
                : <Clock size={14} aria-hidden className="text-muted-foreground" />}
            </ItemMedia>
            <ItemContent className="flex-row items-center gap-2">
              {q.author ? (
                <span data-testid="chat-queued-author" title={interpolate(t.chat.queuedBy, { name: q.author.name })} className="max-w-32 shrink-0 truncate text-foreground">{q.author.name}</span>
              ) : null}
              <span className="min-w-0 flex-1 truncate text-muted-foreground">{q.text}</span>
            </ItemContent>
            {q.removable || q.canSteer ? (
              <ItemActions>
                {q.canSteer ? <Button variant="ghost" size="icon-sm" icon={ArrowUp} onClick={() => steer(q.id)} disabled={pendingIds.has(q.id)} aria-label={t.brainChat.ux.deliveryNow} title={t.brainChat.ux.deliveryNow} className="-my-1.5" /> : null}
                {q.removable ? <Button variant="ghost" size="icon-sm" icon={X} onClick={() => onRemove(q.id)}
                  aria-label={t.brainChat.removeFromQueue} title={t.brainChat.removeFromQueue} disabled={pendingIds.has(q.id)} className="-my-1.5 -mr-1.5" /> : null}
              </ItemActions>
            ) : null}
          </Item>
        ))}
      </ItemGroup>
    </div>
  );
}

/** The composer's `@` in the bound conversation the viewer may write in, shared or not yet: the members
 *  to mention (the viewer excluded) and, for its owner, the people who could still be invited, read from
 *  the conversation's invitable list (every other human account of the instance) only
 *  once the first `@` asks for them. A guest never invites, so a guest's list is the members alone. A shared
 *  conversation also offers the agent itself, whose mention wakes it for that one message while paused. */
function useComposerMentions(): ComposerMentions | undefined {
  const { t } = useTranslation();
  const { activeSessionId } = useBrainChat();
  const { membership, viewerId, readOnly, childFocus } = useBrainChatStatus();
  const writable = membership !== null && membership.capabilities.continue && !readOnly && !childFocus;
  const owner = writable && membership.isOwner;
  const [asked, setAsked] = useState<string | null>(null);
  const invitable = useInvitable(activeSessionId, owner && asked !== null && asked === activeSessionId);
  const { mutateAsync: inviteParticipant } = useInviteParticipant();
  return useMemo(() => {
    if (!writable || !activeSessionId) return undefined;
    const members = [membership.owner, ...membership.participants];
    // The invitable list was read before any invite this `@` made; a person who joined since is a member now.
    const joined = new Set(members.map((member) => member.id));
    return {
      agent: membership.participants.length > 0 ? { paused: membership.agentPaused } : null,
      participants: members.filter((member) => member.id !== viewerId),
      candidates: owner ? (invitable.data?.candidates ?? []).filter((profile) => !joined.has(profile.id)) : [],
      loadCandidates: () => { if (owner) setAsked(activeSessionId); },
      invite: (person) => inviteParticipant({ sessionId: activeSessionId, userId: person.id })
        .then(() => undefined, (error: unknown) => { throw new Error(inviteRefusal(error, t.chat).message); }),
    };
  }, [writable, owner, activeSessionId, membership, viewerId, invitable.data, inviteParticipant, t]);
}

function FooterMemberPicker({ sessionId, members, canInvite, canLeave }: { sessionId: string; members: Profile[]; canInvite: boolean; canLeave: boolean }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const controller = useConversationListController('mine', { search: '', pageIds: [] });
  return <>
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild><Button type="button" variant="ghost" size="sm" aria-label={t.chat.participants}>
        <AvatarStack users={members.map(profileAvatarUser)} max={5} />
      </Button></PopoverTrigger>
      <PopoverContent side="top" align="start" className="w-[calc(100vw-var(--spacing)*6)] md:w-80">
        {open ? <ConversationMembersPopover sessionId={sessionId} canInvite={canInvite} onClose={() => setOpen(false)} onLeave={canLeave ? () => { setOpen(false); setLeaving(true); } : undefined} /> : null}
      </PopoverContent>
    </Popover>
    <ConfirmDialog open={leaving} title={t.chat.leaveConfirm} description={t.chat.leaveConfirmBody} confirmLabel={t.chat.leaveConversation}
      failureMessage={() => t.chat.participantChangeFailed} reload={controller.reload}
      onConfirm={async () => { if (await controller.leave(sessionId)) setLeaving(false); }} onClose={() => setLeaving(false)} />
  </>;
}

/** The composer footer: statusline + staged attachments + queue + the same composer, locked in read-only views.
 *
 *  In both variants it is a non-scrolling flex child below the transcript scroller. The full chat surface consumes
 *  the visual-viewport inset as part of its own height, so the composer reaches the visible band without a
 *  sticky position or a page scroll. `.chat-composer-dock` owns only the safe-area padding that keeps the
 *  send button clear of a phone's home indicator.
 *
 *  No hairline and NO fade above the footer: a gradient over the transcript's last lines read as "there is
 *  more below" and had readers scrolling for text that was never hidden. The dock's own opaque background
 *  is the only edge.
 *
 *  Memoized, and reading only persistent cards, actions and status: everything here moves on a user action
 *  or daemon event, never per token, so a streaming reply leaves the Todo, statusline, model picker and
 *  editor untouched. */
export const ChatFooterDock = memo(function ChatFooterDock({ variant, dockRef, composerRef, cards, live, telemetryShown, statuslineShown, onToggleStatusline, keyboardStatus, onJumpToMessage }: {
  variant: 'full' | 'compact';
  dockRef: RefObject<HTMLDivElement | null>;
  composerRef: RefObject<HTMLTextAreaElement | null>;
  cards: readonly BrainCard[];
  live: boolean;
  telemetryShown?: boolean;
  statuslineShown: boolean;
  onToggleStatusline: () => void;
  keyboardStatus?: ReactNode;
  /** The surface's own scroll-and-reveal jump, which a pending reply's quote uses; it rejects when the target
   *  cannot be reached. */
  onJumpToMessage: (id: string) => Promise<void>;
}) {
  const { t, locale } = useTranslation();
  const { sessions, activeSessionId, removeAttachment, cancelUpload, onQueueRemove, onQueueSteer, restoreRecalledInput, cancelReply, pluginPicker, pluginPickerCrashed, closePluginPicker, retryConnection } = useBrainChat();
  const { notice, ask, usage, goal, lineCfg, fast, attachments, uploads, queued, readOnly, childFocus, connectionState, connectionFirstAttempt, connectionFailure, modelRetry, executionState, membership, telemetry, replyTarget, recalledInputs } = useBrainChatStatus();
  const computer = telemetry.project?.identity;
  const waitingComputer = computer && computer.id === executionState?.projectRef.projectId ? computer.name : undefined;
  const modelHost = useModelControlHost(statuslineShown);
  const mentions = useComposerMentions();
  const active = sessions.data?.find((s) => s.id === activeSessionId);
  // A visible docked rail already reports the goal, so the statusline does not repeat it — the surface's
  // `railOwnsLiveWork` rule, applied to the one row that can show it here.
  const activeSurfaceGoal = goal?.status === 'active' && telemetryShown !== true ? goal : null;
  const hasStatuslineStats = statuslineHasStats(lineCfg);
  // A recovery that succeeds on its first attempt says nothing; the status appears once that attempt fails.
  const showConnection = connectionFailure !== null || (connectionState !== null && !connectionFirstAttempt
    && (connectionState.freshness !== 'fresh' || connectionState.transport !== 'connected' || connectionState.readiness !== 'ready'));
  const showModelRetry = modelRetry !== null && modelRetryShown(modelRetry);
  // Every member of a paused shared conversation sees it while writing; the membership resync clears it live.
  const agentPaused = membership?.agentPaused === true && !readOnly && !childFocus;
  const members = membership && membership.participants.length > 0 && !childFocus
    ? [membership.owner, ...membership.participants] : null;
  const failureLabels = {
    start: t.connectionFailure.startFailed,
    policy: t.connectionFailure.policyUnavailable,
    stream: t.connectionFailure.streamFailed,
  };
  const cacheHit = cacheHitPct({
    cacheRead: usage?.cacheRead,
    cacheWrite: usage?.cacheWrite,
    input: usage?.input,
  });

  return (
    <div ref={dockRef} data-testid="chat-composer-dock" className={variant === 'full' ? 'chat-composer-dock z-[var(--z-lift)] bg-background' : ''}>
      {showConnection || showModelRetry || executionState || agentPaused ? (
        <div className={`flex min-w-0 flex-col gap-1 py-1.5 ${variant === 'full' ? 'chat-gutter' : 'px-3'}`}>
          {showConnection ? (
            <div data-testid="chat-connection-status">
              <ConnectionStatus
                // A setup/reader failure cannot attest to the current transport state.
                connectionState={connectionFailure ? null : connectionState}
                // Live retry text already names the connection; a bare transport state needs the lane name.
                label={connectionFailure ? failureLabels[connectionFailure.source]
                  : connectionState?.freshness === 'fresh' && connectionState.retry ? undefined : t.connectionStatus.transport}
                onRetry={connectionFailure?.source === 'start' ? undefined : retryConnection}
              />
            </div>
          ) : null}
          {showModelRetry ? (
            <div data-testid="chat-model-retry">
              <ConnectionStatus retry={modelRetry} label={t.connectionStatus.model} />
            </div>
          ) : null}
          {executionState?.phase === 'waiting_for_computer' ? (
            <span data-testid="chat-execution-wait" role="status" aria-live="polite" aria-atomic="true" className="text-meta text-muted-foreground">
              {waitingComputer ? interpolate(t.connectionStatus.computerOffline, { computer: waitingComputer }) : t.connectionStatus.waitingForComputer}
            </span>
          ) : null}
          {agentPaused ? (
            <span data-testid="chat-agent-paused" role="status" className="flex items-center gap-1.5 text-meta text-muted-foreground">
              <Pause size={12} aria-hidden className="shrink-0" />
              {t.chat.agentPaused}
            </span>
          ) : null}
        </div>
      ) : null}
      {/* One-line server status notice when the daemon sends one. The running state itself is signalled by
          the composer's Stop button (no separate "thinking" spinner). Hidden while a question is pending. */}
      {notice && !ask ? (
        <div className={`flex items-center gap-2 py-1.5 text-muted-foreground ${variant === 'full' ? 'chat-gutter text-small' : 'px-3 text-small'}`}>
          <span className="italic opacity-80">{notice}</span>
        </div>
      ) : null}
      {/* Todo is persistent conversation state, not part of any message. Keep it in the non-scrolling footer
          directly above the statusline so a live answer remains the transcript tail and every token cannot
          move the checklist through the conversation. */}
      {cards.length > 0 ? (
        <div
          data-testid="chat-footer-cards"
          className={`flex flex-col gap-3 py-2 ${variant === 'full' ? 'chat-gutter text-small' : 'px-3 text-small'}`}
        >
          {cards.map((card) => <PluginCardBlock key={card.id} card={card} live={live} />)}
        </div>
      ) : null}
      {members && membership && activeSessionId ? (
        <div data-testid="chat-statusline-members" className={`min-w-0 pt-1 ${variant === 'full' ? 'chat-gutter' : 'px-3'}`}>
          <div className="pl-5 sm:pl-6">
            {membership.capabilities.invite || membership.capabilities.leave
              ? <FooterMemberPicker key={activeSessionId} sessionId={activeSessionId} members={members} canInvite={membership.capabilities.invite} canLeave={membership.capabilities.leave} />
              : <AvatarStack users={members.map(profileAvatarUser)} max={5} />}
          </div>
        </div>
      ) : null}
      {/* Statusline (the statusline plugin's toggles decide what shows; hidden when disabled). A leading
          chevron collapses the whole row in-chat — the quick alternative to the plugin's settings, mainly
          for a phone where the metrics crowd the composer. Collapsed leaves only the chevron to bring it
          back. */}
      {activeSurfaceGoal || hasStatuslineStats ? (
        // Exactly ONE line, phone included: a second row here pushes the composer down and eats the little
        // vertical room a phone has. The goal is the high-priority prefix; optional statistics give way,
        // trailing first and whole, when they do not fit (the clipped wrap on `.chat-statusline` in chat.css).
        <div data-testid="chat-statusline" className={`chat-statusline flex min-w-0 items-center gap-x-2 overflow-hidden tabular-nums text-muted-foreground sm:gap-x-3 ${variant === 'full' ? 'chat-gutter text-small' : 'px-3 text-small'}`}>
          {hasStatuslineStats ? (
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              onClick={onToggleStatusline}
              aria-expanded={statuslineShown}
              aria-label={statuslineShown ? t.chat.hideStats : t.chat.showStats}
              title={statuslineShown ? t.chat.hideStats : t.chat.showStats}
              className="-my-1 -ml-2 shrink-0 hover:bg-transparent hover:text-foreground"
            >
              <ChevronRight size={11} aria-hidden className={`opacity-60 transition-transform ${statuslineShown ? 'rotate-90' : ''}`} />
            </Button>
          ) : null}
          {activeSurfaceGoal ? <GoalStatusInline goal={activeSurfaceGoal} /> : null}
          {hasStatuslineStats && statuslineShown && lineCfg ? (
            <>
              {/* The model is a CONTROL here, not a read-out: the same picker the top bar used to carry,
                  opening the same grouped catalog. The bar gives its copy up while this one is on screen
                  (see `useModelControlHost`), so the conversation has exactly one model switcher.
                  A conversation the controller has not adopted yet — a history entry being previewed — has
                  no model to switch, so that case stays the plain label it always was. */}
              {modelHost === 'statusline' ? (
                <ModelPicker variant="statusline" />
              ) : lineCfg.showModel && active?.model ? (
                <span
                  data-stat="model"
                  className="min-w-0 truncate"
                  title={brainModelQualifiedLabel({ provider: active.provider ?? '', providerLabel: '', model: active.model })}
                >
                  {brainModelLabel({ provider: active.provider ?? '', providerLabel: '', model: active.model })}
                </span>
              ) : null}
              {/* The model runs on its Fast route: the same tag the CLI statusline shows. Not localized, like
                  the row's other symbols. */}
              {fast ? <span data-stat="fast" className="shrink-0 text-small-emphasis text-primary">FAST</span> : null}
              {lineCfg.showContext && usage && usage.percent != null ? (
                <span data-stat="context" className="shrink-0 whitespace-nowrap">{t.brainChat.context} {contextMeter(usage.percent, 'raw').label} ({formatTokens(usage.tokens ?? 0)}/{formatTokens(usage.contextWindow)})</span>
              ) : null}
              {lineCfg.showTokens && usage ? <span data-stat="tokens" className="shrink-0 whitespace-nowrap">Σ {formatTokens(usage.totalTokens)} {t.sessionsPanel.tok}</span> : null}
              {/* Current-turn generated output, including reasoning and tool calls, over successful provider
                  generation time. Queue/header wait, failed retries, tools and human waits are excluded.
                  Shared formatting preserves positive fractional measurements. */}
              {lineCfg.showSpeed && typeof usage?.effectiveTps === 'number' && usage.effectiveTps > 0 ? (
                <span data-stat="speed" className="shrink-0 whitespace-nowrap">{formatSpeed(usage.effectiveTps, locale)}</span>
              ) : null}
              {/* Same wording as the CLI statusline, which is not localized: the row's other segments
                  (tokens, cost) are symbols too, so a translated phrase would stand out here. */}
              {lineCfg.showCacheHit && cacheHit != null ? (
                <span data-stat="cache-hit" className="shrink-0 whitespace-nowrap">cache {Math.round(cacheHit)}%</span>
              ) : null}
              {lineCfg.showCost && usage ? <span data-stat="cost" className="shrink-0 whitespace-nowrap">{formatCost(usage.cost, 2)}</span> : null}
            </>
          ) : null}
        </div>
      ) : null}

      {/* Staged attachments, and the files still uploading behind them. */}
      {attachments.length > 0 || uploads.length > 0 ? (
        <div className={`flex flex-wrap gap-2 py-2 ${variant === 'full' ? 'chat-gutter' : 'px-3'}`}>
          {attachments.map((a, i) => (
            /* No thumbnail: the file is already on the daemon, not held in the browser, so there is
               nothing local to preview. The title names where it landed, which is what the user
               actually needs — the file stays in their project after the conversation. */
            <span key={i} title={a.relative} className="inline-flex items-center gap-1.5 rounded-md border border-border bg-muted py-1 pl-1.5 pr-1 text-small text-foreground">
              <FileText size={13} className="text-muted-foreground" aria-hidden />
              <span className="max-w-35 truncate">{a.name}</span>
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                onClick={() => removeAttachment(i)}
                aria-label={t.brainChat.attachRemove}
                className="-mx-2 -my-1.5 hover:bg-transparent hover:text-foreground"
              >
                <X size={11} aria-hidden />
              </Button>
            </span>
          ))}
          {uploads.map((u) => <UploadChip key={`upload-${u.key}`} upload={u} onCancel={() => cancelUpload(u.key)} />)}
        </div>
      ) : null}

      {/* Pending mid-turn queue: messages sent while a turn streams, parked until it ends. Removable
          until delivered; hidden while a focused session replaces the bound conversation's view (a
          focused child's sends do not queue, and a read-only preview has no composer at all). */}
      {!readOnly && !childFocus && queued.length > 0 ? (
        <QueueChips key={activeSessionId} entries={queued} onRemove={onQueueRemove} onSteer={onQueueSteer}
          className={`py-2 ${variant === 'full' ? 'chat-gutter' : 'px-3'}`} />
      ) : null}

      {!readOnly && !childFocus && activeSessionId && recalledInputs.length > 0 ? (
        <div className={`py-1 ${variant === 'full' ? 'chat-gutter' : 'px-3'}`}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild><Button type="button" variant="ghost" size="default" data-testid="chat-recalled-trigger" aria-label={`${t.brainChat.ux.recalledInputsLabel}: ${interpolate(t.brainChat.ux.recalledInputsCount, { count: recalledInputs.length })}`}>
              {interpolate(t.brainChat.ux.recalledInputsCount, { count: recalledInputs.length })}
            </Button></DropdownMenuTrigger>
            <DropdownMenuContent align="start" aria-label={t.brainChat.ux.recalledInputsLabel} className="max-w-[var(--radix-dropdown-menu-content-available-width)]">
              {recalledInputs.map(row => <RecalledChoice key={row.id} row={row} session={activeSessionId} owner={membership?.owner ?? null} onRestore={restoreRecalledInput} />)}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ) : null}

      {/* A plugin picker that cannot open, or crashed, says so here, in the surface, instead of below the app shell. */}
      {pluginPicker ? (
        <div className={`py-2 empty:hidden ${variant === 'full' ? 'chat-gutter' : 'px-3'}`}>
          <PluginChatPickerNotice invocation={pluginPicker} crashed={pluginPickerCrashed} closeAction={closePluginPicker} />
        </div>
      ) : null}

      <div className={`flex items-center py-1 empty:hidden ${variant === 'full' ? 'chat-gutter' : 'px-3'}`}>
        {keyboardStatus}
      </div>

      <div className={variant === 'full' ? 'chat-gutter chat-composer-slot' : ''}>
        {!readOnly && !childFocus && replyTarget && replyTarget.sessionId === activeSessionId ? <PendingReply session={replyTarget.sessionId} messageId={replyTarget.messageId} owner={membership?.owner ?? null} onCancel={() => { cancelReply(); composerRef.current?.focus(); }} onJump={() => onJumpToMessage(replyTarget.messageId)} /> : null}
        {/* Read-only views keep the same editor footprint and controls; only writing is locked. */}
        <ChatComposer variant={variant} composerRef={composerRef} mentions={mentions} />
      </div>
    </div>
  );
});
